/**
 * Orden de trabajo: la entidad central del field service.
 *
 * §30 del prompt maestro. Su máquina de estados (§26) tiene 13 estados
 * porque el trabajo de campo real no es «pendiente → hecho»: hay
 * desplazamientos, pausas, trabajos parciales y tres formas distintas de
 * quedarse a medias (falta la firma, falta material, falta presupuesto).
 *
 * Modelar esos estados explícitamente es lo que permite responder «¿por qué
 * lleva esta OT nueve días abierta?» sin llamar al técnico.
 */

import { ValidationError } from './errors.js';

export const WORK_ORDER_STATUSES = [
  'PENDIENTE',
  'PROGRAMADA',
  'ASIGNADA',
  'DESPLAZAMIENTO',
  'INICIADA',
  'PAUSADA',
  'PARCIAL',
  'TERMINADA',
  'PENDIENTE_FIRMA',
  'PENDIENTE_MATERIAL',
  'PENDIENTE_PRESUPUESTO',
  'CERRADA',
  'FACTURADA',
  'ANULADA',
] as const;

export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number];

export const WORK_ORDER_TYPES = [
  /** Mantenimiento preventivo derivado del plan. */
  'PREVENTIVO',
  /** Correctivo derivado de una anomalía. */
  'CORRECTIVO',
  /** Aviso del cliente (SAT). */
  'AVISO',
  'INSTALACION',
  'TALLER',
  'OTROS',
] as const;
export type WorkOrderType = (typeof WORK_ORDER_TYPES)[number];

export const WORK_ORDER_PRIORITIES = ['URGENTE', 'ALTA', 'MEDIA', 'BAJA'] as const;
export type WorkOrderPriority = (typeof WORK_ORDER_PRIORITIES)[number];

/** Transiciones permitidas. Lo que no está aquí, no ocurre. */
const TRANSITIONS: Readonly<Record<WorkOrderStatus, readonly WorkOrderStatus[]>> = {
  PENDIENTE: ['PROGRAMADA', 'ASIGNADA', 'ANULADA'],
  PROGRAMADA: ['ASIGNADA', 'PENDIENTE', 'ANULADA'],
  // Se puede reasignar (vuelve a PROGRAMADA) mientras no haya empezado
  ASIGNADA: ['DESPLAZAMIENTO', 'INICIADA', 'PROGRAMADA', 'ANULADA'],
  DESPLAZAMIENTO: ['INICIADA', 'ASIGNADA', 'ANULADA'],
  INICIADA: ['PAUSADA', 'PARCIAL', 'TERMINADA', 'ANULADA'],
  PAUSADA: ['INICIADA', 'PARCIAL', 'ANULADA'],
  // Un trabajo parcial se retoma con otra visita
  PARCIAL: ['PROGRAMADA', 'ASIGNADA', 'INICIADA', 'TERMINADA', 'ANULADA'],
  TERMINADA: ['PENDIENTE_FIRMA', 'PENDIENTE_MATERIAL', 'PENDIENTE_PRESUPUESTO', 'CERRADA'],
  PENDIENTE_FIRMA: ['CERRADA', 'PENDIENTE_MATERIAL', 'PENDIENTE_PRESUPUESTO'],
  PENDIENTE_MATERIAL: ['PROGRAMADA', 'ASIGNADA', 'CERRADA', 'PENDIENTE_PRESUPUESTO'],
  PENDIENTE_PRESUPUESTO: ['PROGRAMADA', 'ASIGNADA', 'CERRADA', 'PENDIENTE_MATERIAL'],
  CERRADA: ['FACTURADA'],
  // Estados terminales
  FACTURADA: [],
  ANULADA: [],
};

/** Estados en los que la OT ya no admite trabajo de campo. */
const TERMINAL: ReadonlySet<WorkOrderStatus> = new Set<WorkOrderStatus>(['FACTURADA', 'ANULADA']);

/** Estados que exigen justificar el cambio. */
const REQUIRES_REASON: ReadonlySet<WorkOrderStatus> = new Set<WorkOrderStatus>([
  'ANULADA',
  'PAUSADA',
  'PARCIAL',
  'PENDIENTE_MATERIAL',
  'PENDIENTE_PRESUPUESTO',
]);

/** Estados en los que el reloj del SLA está parado. */
const SLA_PAUSED: ReadonlySet<WorkOrderStatus> = new Set<WorkOrderStatus>([
  'PENDIENTE_MATERIAL',
  'PENDIENTE_PRESUPUESTO',
]);

export function canTransition(from: WorkOrderStatus, to: WorkOrderStatus): boolean {
  return (TRANSITIONS[from] ?? []).includes(to);
}

export function allowedTransitions(from: WorkOrderStatus): readonly WorkOrderStatus[] {
  return TRANSITIONS[from] ?? [];
}

export function isTerminal(status: WorkOrderStatus): boolean {
  return TERMINAL.has(status);
}

export function requiresReason(status: WorkOrderStatus): boolean {
  return REQUIRES_REASON.has(status);
}

export function isSlaPaused(status: WorkOrderStatus): boolean {
  return SLA_PAUSED.has(status);
}

/** ¿La OT sigue viva para el técnico en campo? */
export function isFieldActive(status: WorkOrderStatus): boolean {
  return ['ASIGNADA', 'DESPLAZAMIENTO', 'INICIADA', 'PAUSADA', 'PARCIAL'].includes(status);
}

// ───────────────────────────────────────────────────────────────────────
// QUÉ SE PUEDE CORREGIR, Y CUÁNDO
// ───────────────────────────────────────────────────────────────────────

/**
 * Los campos de una orden que se pueden corregir después de crearla.
 *
 * Hasta ahora no se podía: una orden mal creada había que anularla y
 * rehacerla, gastando un número del correlativo por una errata en un
 * teléfono. Pero «editable» no puede significar «todo, siempre»: el número,
 * el estado y las horas fichadas son el rastro de lo que pasó, y eso no se
 * corrige, se rectifica con otra operación que deja constancia.
 */
export const WORK_ORDER_EDITABLE_FIELDS = [
  'workOrderType',
  'priority',
  'scheduledDate',
  'scheduledTime',
  'estimatedMinutes',
  'legalDeadline',
  'contractId',
  'contactName',
  'contactPhone',
  'instructions',
  'notes',
] as const;

export type WorkOrderEditableField = (typeof WORK_ORDER_EDITABLE_FIELDS)[number];

/**
 * Lo que deja de poder corregirse cuando el técnico ya está en camino.
 *
 * Cambiarle la fecha o el plazo a una orden que alguien está ejecutando en
 * ese momento no es una corrección: es mover el suelo bajo sus pies. El
 * teléfono del contacto o las instrucciones sí —son justo lo que hace falta
 * arreglar cuando el técnico llama diciendo que nadie abre.
 */
const BLOQUEADOS_EN_CAMPO: ReadonlySet<string> = new Set([
  'workOrderType',
  'scheduledDate',
  'scheduledTime',
  'legalDeadline',
  'contractId',
]);

/**
 * La orden ya no admite correcciones.
 *
 * `isTerminal` no vale aquí: para la máquina de estados CERRADA todavía
 * tiene salida —se puede facturar— pero el trabajo ya está hecho y firmado,
 * y a efectos de corregir datos es tan definitiva como las otras dos.
 */
function yaNoSeToca(status: WorkOrderStatus): boolean {
  return isTerminal(status) || status === 'CERRADA';
}

export interface EditPermission {
  readonly allowed: boolean;
  /** Por qué no, en una frase que se pueda enseñar tal cual. */
  readonly reason?: string;
}

/**
 * ¿Se puede corregir este campo con la orden en este estado?
 *
 * Una orden cerrada, facturada o anulada no se toca: lo que dice es lo que
 * se facturó o lo que se declaró que no se hizo. Corregirla a posteriori
 * convierte el histórico en algo que no se puede defender ante nadie.
 */
/** La misma negativa, dicha como la entiende quien la lee. */
function motivoCerrada(status: WorkOrderStatus): string {
  if (status === 'FACTURADA') return 'La orden ya está facturada: lo que dice es lo que se cobró.';
  if (status === 'ANULADA') return 'La orden está anulada. Si hay que rehacer el trabajo, se abre otra.';
  return 'La orden está cerrada y ya no se corrige.';
}

export function canEditWorkOrderField(
  status: WorkOrderStatus,
  field: WorkOrderEditableField,
): EditPermission {
  if (yaNoSeToca(status)) {
    return { allowed: false, reason: motivoCerrada(status) };
  }

  if (isFieldActive(status) && BLOQUEADOS_EN_CAMPO.has(field)) {
    return {
      allowed: false,
      reason: 'El técnico ya está con esta orden: esto se cambia antes de asignarla.',
    };
  }

  return { allowed: true };
}

/**
 * ¿Se puede mover la orden a otro centro?
 *
 * Sólo antes de que nadie haya salido. Una orden que ya se está ejecutando
 * pertenece al sitio donde está el técnico, y los equipos que lleva dentro
 * son los de ese centro: cambiarle la sede dejaría una orden que dice haber
 * revisado extintores que están en otro edificio.
 */
export function canMoveWorkOrder(status: WorkOrderStatus): EditPermission {
  if (status === 'PENDIENTE' || status === 'PROGRAMADA' || status === 'ASIGNADA') {
    return { allowed: true };
  }
  return {
    allowed: false,
    reason: yaNoSeToca(status)
      ? 'La orden ya está terminada: no se mueve de centro.'
      : 'La orden ya está en ejecución: no se mueve de centro.',
  };
}

export interface TransitionInput {
  readonly from: WorkOrderStatus;
  readonly to: WorkOrderStatus;
  readonly reason?: string | null;
}

/**
 * Valida un cambio de estado.
 *
 * Lanza en lugar de devolver un booleano porque el mensaje importa: el
 * usuario necesita saber a qué estados SÍ puede pasar, no sólo que ha
 * fallado.
 */
export function assertTransition(input: TransitionInput): void {
  const { from, to, reason } = input;

  if (from === to) {
    throw new ValidationError(`La orden de trabajo ya está en estado ${from}`);
  }

  if (isTerminal(from)) {
    throw new ValidationError(
      `Una orden de trabajo ${from} es un estado final y no admite más cambios`,
    );
  }

  if (!canTransition(from, to)) {
    const allowed = allowedTransitions(from);
    throw new ValidationError(
      `Transición no permitida: ${from} → ${to}. Desde ${from} sólo puede pasar a: ${allowed.join(', ') || '(ninguno)'}`,
    );
  }

  if (requiresReason(to) && !reason?.trim()) {
    throw new ValidationError(`Pasar a ${to} exige indicar el motivo`);
  }
}

// ═══════════════════════════════════════════════════════════════════════
// SLA
// ═══════════════════════════════════════════════════════════════════════

export interface SlaConfig {
  readonly urgentHours?: number | null;
  readonly highHours?: number | null;
  readonly normalHours?: number | null;
}

export const DEFAULT_SLA: Required<SlaConfig> = {
  urgentHours: 4,
  highHours: 24,
  normalHours: 72,
};

export interface SlaStatus {
  readonly hasSla: boolean;
  readonly deadline: string | null;
  readonly hoursRemaining: number | null;
  readonly breached: boolean;
  /** Queda menos del 25 % del plazo: aparece en el panel «SLA EN RIESGO». */
  readonly atRisk: boolean;
  readonly paused: boolean;
  readonly message: string;
}

function hoursForPriority(priority: WorkOrderPriority, sla: SlaConfig): number | null {
  switch (priority) {
    case 'URGENTE':
      return sla.urgentHours ?? null;
    case 'ALTA':
      return sla.highHours ?? null;
    case 'MEDIA':
      return sla.normalHours ?? null;
    case 'BAJA':
      return null; // sin compromiso de plazo
  }
}

/**
 * Estado del SLA de una OT.
 *
 * El reloj se PARA en los estados que dependen del cliente o de un tercero
 * (pendiente de material, pendiente de presupuesto): penalizar a la
 * empresa por un presupuesto que el cliente no contesta convertiría el
 * indicador en ruido.
 */
export function slaStatus(input: {
  priority: WorkOrderPriority;
  openedAt: string;
  status: WorkOrderStatus;
  sla?: SlaConfig;
  now: string;
  /** Horas acumuladas con el reloj parado. */
  pausedHours?: number;
}): SlaStatus {
  // OJO: un `null` en la configuración NO significa «sin SLA», significa
  // «no configurado»: hay que caer al valor por defecto. Hacer
  // `{ ...DEFAULT_SLA, ...input.sla }` pisaba los defectos con los nulls
  // que llegan de un contrato sin columnas de SLA rellenas, y desactivaba
  // en silencio TODO el seguimiento de plazos.
  // Para expresar «sin compromiso de plazo» está la prioridad BAJA.
  const sla: Required<SlaConfig> = {
    urgentHours: input.sla?.urgentHours ?? DEFAULT_SLA.urgentHours,
    highHours: input.sla?.highHours ?? DEFAULT_SLA.highHours,
    normalHours: input.sla?.normalHours ?? DEFAULT_SLA.normalHours,
  };
  const hours = hoursForPriority(input.priority, sla);

  if (hours === null) {
    return {
      hasSla: false,
      deadline: null,
      hoursRemaining: null,
      breached: false,
      atRisk: false,
      paused: false,
      message: 'Sin compromiso de plazo',
    };
  }

  const opened = Date.parse(input.openedAt);
  const now = Date.parse(input.now);
  if (Number.isNaN(opened) || Number.isNaN(now)) {
    throw new ValidationError('Las fechas del SLA deben ser instantes ISO-8601 válidos');
  }

  const paused = isSlaPaused(input.status);
  const deadlineMs = opened + (hours + (input.pausedHours ?? 0)) * 3_600_000;
  const deadline = new Date(deadlineMs).toISOString();

  // Una OT ya cerrada no puede seguir incumpliendo: se congela.
  if (['CERRADA', 'FACTURADA', 'TERMINADA'].includes(input.status)) {
    return {
      hasSla: true,
      deadline,
      hoursRemaining: null,
      breached: false,
      atRisk: false,
      paused: false,
      message: 'Trabajo finalizado',
    };
  }

  const hoursRemaining = (deadlineMs - now) / 3_600_000;
  const breached = !paused && hoursRemaining < 0;
  const atRisk = !paused && !breached && hoursRemaining <= hours * 0.25;

  return {
    hasSla: true,
    deadline,
    hoursRemaining: Math.round(hoursRemaining * 10) / 10,
    breached,
    atRisk,
    paused,
    message: paused
      ? 'SLA en pausa (a la espera del cliente o de material)'
      : breached
        ? `SLA incumplido hace ${Math.abs(Math.round(hoursRemaining))} h`
        : `Quedan ${Math.round(hoursRemaining)} h de SLA`,
  };
}

// ═══════════════════════════════════════════════════════════════════════
// Registro de tiempos
// ═══════════════════════════════════════════════════════════════════════

export const TIME_ENTRY_KINDS = ['DESPLAZAMIENTO', 'TRABAJO', 'PAUSA', 'RETORNO'] as const;
export type TimeEntryKind = (typeof TIME_ENTRY_KINDS)[number];

export interface TimeEntry {
  readonly kind: TimeEntryKind;
  readonly startedAt: string;
  readonly endedAt: string | null;
}

export interface TimeSummary {
  /** Horas con dos decimales, como cadena: alimenta el coste de la OT. */
  readonly travelHours: string;
  readonly workHours: string;
  readonly pauseHours: string;
  readonly totalBillableHours: string;
  readonly openEntries: number;
}

function hoursBetween(from: string, to: string): number {
  return (Date.parse(to) - Date.parse(from)) / 3_600_000;
}

/**
 * Resume los tiempos de una OT.
 *
 * Devuelve cadenas y no `number` porque estas horas se multiplican por un
 * precio: entran directamente en el cálculo de coste y margen, donde la
 * aritmética de coma flotante no tiene sitio (ADR-001).
 */
export function summarizeTime(entries: readonly TimeEntry[], now?: string): TimeSummary {
  let travel = 0;
  let work = 0;
  let pause = 0;
  let open = 0;

  for (const entry of entries) {
    const end = entry.endedAt ?? now ?? null;
    if (!entry.endedAt) open++;
    if (!end) continue;

    const hours = Math.max(0, hoursBetween(entry.startedAt, end));
    switch (entry.kind) {
      case 'DESPLAZAMIENTO':
      case 'RETORNO':
        travel += hours;
        break;
      case 'TRABAJO':
        work += hours;
        break;
      case 'PAUSA':
        pause += hours;
        break;
    }
  }

  const fmt = (h: number): string => h.toFixed(2);

  return {
    travelHours: fmt(travel),
    workHours: fmt(work),
    pauseHours: fmt(pause),
    // La pausa NO se factura; el desplazamiento sí, si el contrato lo prevé.
    totalBillableHours: fmt(travel + work),
    openEntries: open,
  };
}
